import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popper,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { AccessTime } from '@mui/icons-material';
import { createSvgIcon } from '@mui/material';
import { ComponentStyles, FontSize, Palette, StylesType } from './FlatFileTableTypes';
import { ColumnInfos, SampleRecords } from '../../redux/FlatFile/FlatFileInterfaces';
import { calcLength } from 'framer-motion';

const BooleanIcon = createSvgIcon(
  <svg viewBox="0 0 76 76" xmlns="http://www.w3.org/2000/svg" fill="rgb(72,72,72)">
    <path d="M 36,23L 30,23L 30,40L 25,40L 25,23L 19,23L 19,19L 36,19L 36,23 Z M 57,40L 50,40L 50,45L 56,45L 56,49L 50,49L 50,57L 45,57L 45,36L 57,36L 57,40 Z M 44,19L 48.5,19L 32.5,57L 28,57L 44,19 Z" />
  </svg>,
  'Boolean',
);

const dataTypes = [
  {
    value: 'text',
    icon: <img src="/Text_Icon.svg" alt="Text" style={{ height: 16, width: 16 }} />,
  },
  {
    value: 'integer',
    icon: (
      <img
        src="/Integer_Icon.svg"
        alt="Integer"
        style={{ height: 16, width: 16, color: 'white' }}
      />
    ),
  },
  {
    value: 'decimal',
    icon: <img src="/Decimal_Icon.svg" alt="Decimal" style={{ height: 16, width: 16 }} />,
  },
  { value: 'boolean', icon: <BooleanIcon sx={{ height: 16, width: 16 }} /> },
  { value: 'date', icon: <CalendarMonthIcon sx={{ height: 14, width: 14 }} /> },
  { value: 'timestamp', icon: <AccessTime sx={{ height: 16, width: 16 }} /> },
];

const getDataTypeIcon = (dataType: string) => {
  const type = dataTypes.find((d) => d.value === dataType.toLowerCase());
  return type?.icon || null;
};

type FlatFileTableProps = {
  columnInfos: ColumnInfos[];
  sampleRecords: SampleRecords[];
  setEditApiResponse: (key: string, value: any) => void;
  isEditMode: boolean;
  classes: ComponentStyles;
  classes2: StylesType;
  fontSize: FontSize;
  palette: Palette;
};

const FlatFileTable = ({
  columnInfos,
  sampleRecords,
  setEditApiResponse,
  classes,
  fontSize,
  isEditMode,
  palette,
}: FlatFileTableProps) => {
  const [activeDropdownField, setActiveDropdownField] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSelectType = (fieldName: string, value: string) => {
    // Don't allow changes in edit mode
    if (isEditMode) {
      return;
    }

    const updated = columnInfos.map((col) =>
      col.fieldName === fieldName ? { ...col, dataType: value } : col,
    );
    setEditApiResponse('columnInfos', updated);
    setActiveDropdownField(null);
    setAnchorEl(null);
  };

  const renderCellValue = (value: any) => {
    if (typeof value === 'boolean') return value ? 'True' : 'False';
    return value;
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>, fieldName: string) => {
    setAnchorEl(e.currentTarget);
    setActiveDropdownField(fieldName);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setActiveDropdownField(null);
  };

  return (
    <TableContainer
      sx={{
        maxWidth: '100%',
        overflow: 'auto',
        borderRadius: '5px',
        minHeight: 'auto',
        maxHeight: '95%',
      }}
    >
      <Table
        stickyHeader
        sx={{
         /*border: '1.5px solid rgb(164, 155, 155)',*/
          width: 'fit-content',
        }}
      >
        <TableHead sx={classes.tableHeader}>
          <TableRow>
            {columnInfos?.map((header) => (
              <TableCell
                key={header.fieldName}
                style={{
                  minWidth: 130,
                  maxWidth: 200,
                  padding: '8px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: 4,
                  }}
                >
                  <div
                    onMouseEnter={(e) => handleMouseEnter(e, header.fieldName)}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      background: 'white',

                      width: 25,
                      height: 20,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    {getDataTypeIcon(header.dataType)}
                  </div>
                </div>

                <div
                  style={{
                    color: 'white',
                    textAlign: 'left',
                    fontSize: fontSize.medium,
                    fontWeight: 'bold',
                  }}
                >
                  {header.fieldName}
                </div>

                <Popper
                  open={activeDropdownField === header.fieldName}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  disablePortal={false}
                  modifiers={[
                    { name: 'offset', options: { offset: [0, 4] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport' } },
                  ]}
                  style={{ zIndex: 9999 }}
                >
                  {activeDropdownField === header.fieldName && (
                    <div
                      onMouseEnter={() => setActiveDropdownField(header.fieldName)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: '140px',
                        padding: '4px 0',
                        transform: 'translateY(-4px)',
                      }}
                    >
                      {dataTypes.map((dt) => {
                        const isSelected = header.dataType === dt.value;
                        return <button
                          key={dt.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: fontSize.medium,
                            padding: '6px 12px',
                            background: 'transparent',
                            border: 'none',
                            width: '100%',
                            textAlign: 'left',
                            cursor: `${isEditMode ? "not-allowed" : 'pointer'}`,
                            transition: 'background 0.2s ease-in-out',
                            fontWeight: isSelected ? 'bold' : 'normal', 
                            color: isSelected ? '#111' : '#333',
                          }}
                           disabled={isEditMode}
                          onMouseEnter={(e) =>
                          (e.currentTarget.style.background = isSelected
                            ? 'rgba(47, 203, 205, 0.3)'
                            : 'rgba(47, 203, 205, 0.1)')
                          }
                          onMouseLeave={(e) =>
                          (e.currentTarget.style.background = isSelected
                            ? 'rgba(47, 203, 205, 0.2)'
                            : 'transparent')
                          }
                          onClick={() => handleSelectType(header.fieldName, dt.value)}
                        >
                          {dt.icon}
                          <span style={{ marginLeft: '8px', color: '#333' }}>
                            {dt.value.charAt(0).toUpperCase() + dt.value.slice(1)}
                          </span>
                        </button>
                      })}
                    </div>
                  )}
                </Popper>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody sx={classes.tableBody}>
          {sampleRecords?.map((record, rowIndex) => (
            <TableRow key={rowIndex} sx={classes.tableBody}>
              {columnInfos.map((col, colIdx) => (
                <TableCell
                  key={colIdx}
                  sx={{
                    '&.MuiTableCell-root': {
                      fontSize: `${fontSize.small} !important`,
                      color: palette.primary.contrastText,
                    },
                  }}
                >
                  {renderCellValue(record[col.fieldName])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FlatFileTable;
